/**
 * 使い方ガイド（/guide・pages/GuidePage.tsx）の置き場所と「できること」の表（2026-10-07 console/guide-page）。
 *
 * WHY: founder（2026-10-07）「使い方ガイドみたいな。簡易的な 1 ページはほしい。簡単なできることも書いてあげたい。通信機能とか、ランキングとか」。
 *   ダッシュボードは「チャットで完結」の方針（2026-10-04）なので、ガイドも「AI エージェントにこう言う」の例文（コピー付き）で書く。
 *   トップバーの「Connect an AI agent」（connectPage.ts）の隣に入口を置き、ホームからもリンクする。
 *
 * 表に載せるのは今 開いている機能だけ（launchHidden.ts の LAUNCH_HIDDEN_FEATURES と skills/INDEX.md に合わせる）。
 *   載せない: 課金（payments）・広告（rewarded-ads）・チャット（chat）・フレンド・メール配信など、隠している機能。
 *   例外 = ランキング: ダッシュボードの機能（leaderboards）としては隠しているが、Skill（skills/leaderboard）で AI が
 *   クリエイター自身の DB と Functions に組む = KUMODeck の出来合いの機能ではない、と文言で正しく言う（ownCode）。
 * 並びは指示書の順（オンライン対戦・ランキング・ログイン・セーブ・サーバーの処理と DB・自分のドメイン・X のカード）。
 * 実在は guidePage.test.ts が確かめる（skill のフォルダと INDEX.md の行）。
 *
 * このファイルは import を持たない（node --test で型の除去だけで動かす・launchHidden.ts と同じ）。
 */

export const GUIDE_PATH = '/guide';

export interface GuideItem {
  /** 文言のキー guide.item.<id>.title / body / ask */
  id: string;
  /** その例文で AI が読む Skill（skills/<skill>/SKILL.md）。例文はこの Skill の「Use when」に合わせて書く */
  skill: string;
  /** コードとデータがクリエイターのもの（KUMODeck が動かす機能ではない）。文言で「your own database」と言う */
  ownCode?: boolean;
}

export const GUIDE_ITEMS: readonly GuideItem[] = [
  { id: 'multiplayer', skill: 'multiplayer' },
  { id: 'leaderboard', skill: 'leaderboard', ownCode: true },
  { id: 'signIn', skill: 'user-login' },
  { id: 'saves', skill: 'user-data' },
  { id: 'functions', skill: 'functions-d1', ownCode: true },
  // 自分のドメインは deploy の §7・X のカードは start（最初から付ける）と deploy が扱う
  { id: 'domain', skill: 'deploy' },
  { id: 'shareCard', skill: 'start' }
];

/** 困ったときの宛先の既定（指示書の指定）。ビルドの env（VITE_KUMO_SUPPORT_EMAIL）で変えられる = productName.ts と同じ形 */
export const DEFAULT_SUPPORT_EMAIL = 'support@kumodeck.com';

/** env の値 → 宛先（メールアドレスの形でなければ既定）。mailto: に入れるので形だけは確かめる */
export function supportEmailFrom(raw: unknown): string {
  const v = typeof raw === 'string' ? raw.trim() : '';
  return /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/.test(v) ? v : DEFAULT_SUPPORT_EMAIL;
}

export const SUPPORT_EMAIL: string = supportEmailFrom((import.meta as { env?: Record<string, unknown> }).env?.VITE_KUMO_SUPPORT_EMAIL);
